import React, { FC, ReactNode } from 'react'
import { matchPath, useLocation } from 'react-router-dom'

import { versionedRoutes } from 'constants/routes'
import { IsEmbedded } from './IsEmbedded'
import { NotEmbedded } from './NotEmbedded'

/**
 * Embeddable routes whitelist.
 */
const embeddableRoutes = [
  versionedRoutes.SHARE_VIEW.LATEST,
  versionedRoutes.SHARE_VIEW.VERSION,
  versionedRoutes.ARTBOARD_DETAIL.LATEST,
  versionedRoutes.ARTBOARD_DETAIL.VERSION,
  versionedRoutes.ARTBOARD_REVISION.LATEST,
  versionedRoutes.ARTBOARD_REVISION.VERSION,
  versionedRoutes.SHARE_PAGE_VIEW.LATEST,
  versionedRoutes.SHARE_PAGE_VIEW.VERSION,
  versionedRoutes.SHARE_PAGE_CANVAS_VIEW.LATEST,
  versionedRoutes.SHARE_PAGE_CANVAS_VIEW.VERSION,
  versionedRoutes.PROTOTYPE.LATEST,
  versionedRoutes.PROTOTYPE.VERSION,
]

/**
 * Determines if the supplied pathname matches one of the whitelisted
 * embeddable routes.
 */
const isPathnameEmbeddable = (pathname: string): boolean =>
  Boolean(
    matchPath(pathname.replace(/^\/embed/, ''), {
      path: embeddableRoutes.map(route => route.template()),
      strict: false,
      exact: true,
    })
  )

type EmbedGateProps = {
  children: ReactNode
}

/**
 * If rendering while embedded, the EmbedGate only renders its children if the
 * initial pathname is strictly one of the whitelisted embedded routes listed
 * above, otherwise it outputs null. While rendering normally/un-embedded it's
 * a no-op and always renders children.
 */
const EmbedGate: FC<EmbedGateProps> = props => {
  const location = useLocation()
  const embeddable = isPathnameEmbeddable(location.pathname)
  return (
    <>
      <IsEmbedded>{embeddable ? props.children : null}</IsEmbedded>
      <NotEmbedded>{props.children}</NotEmbedded>
    </>
  )
}

export { EmbedGate }
